import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { CircleDot, Info } from 'lucide-react';
import { SkeletonTableRows } from '../ui/kit';
import PageHeader from '../components/PageHeader';

// Fahrer-Leistung (Fahrer-Paket A1): Wochen-Zahlen MONTAG–MONTAG je Fahrer als Ampel.
// Datenquelle: GET /partners/driver-performance — dieselbe Rechnung sieht jeder Fahrer
// über sich selbst in der Fahrer-App (EU-Transparenz). Der Partner führt seine Leute
// selbst: Online-Zeit (v. a. in Peak-Fenstern), Annahmequote und Storno sind Teil des Jobs.

const GREEN = '#16a34a', AMBER = '#d97706', RED = '#ef4444', GREY = '#6b7280';

// Ampel-Schwellen (Anzeige-Logik; die Status-Gates 80 %/10 %/4,75 setzt der Admin zentral).
const colorAccept = (v) => (v == null ? GREY : v >= 80 ? GREEN : v >= 60 ? AMBER : RED);
const colorCancel = (v) => (v == null ? GREY : v <= 10 ? GREEN : v <= 20 ? AMBER : RED);
const colorRating = (v) => (v == null ? GREY : v >= 4.75 ? GREEN : v >= 4.5 ? AMBER : RED);

function Pill({ value, suffix = '', color }) {
  return (
    <span style={{ display: 'inline-block', minWidth: 52, textAlign: 'center', padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 800, color: '#fff', background: color }}>
      {value == null ? '–' : `${value}${suffix}`}
    </span>
  );
}

const DAY_NAMES = { 1: 'Mo', 2: 'Di', 3: 'Mi', 4: 'Do', 5: 'Fr', 6: 'Sa', 7: 'So' };
const windowLabel = (w) => `${(w.days || []).map(d => DAY_NAMES[d]).join('/')} ${w.from}–${w.to} Uhr`;

export default function DriverPerformance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/partners/driver-performance')
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const drivers = data?.drivers || [];
  const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 800, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
  const td = { padding: '12px', fontSize: 13, color: '#0a0a0a', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' };

  return (
    <div style={{ maxWidth: 1000 }}>
      <PageHeader
        title="Fahrer-Leistung"
        description="Aktuelle Gesamtleistung deiner Fahrer. Online-/Peak-Stunden und Tagesdurchschnitt beziehen sich auf die laufende Woche."
      />

      {data?.peak_windows?.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#4b5563' }}>
          <Info size={14} />
          <span><strong>Peak-Fenster</strong> (zählen für die Peak-Stunden): {data.peak_windows.map(windowLabel).join(' · ')}</span>
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Fahrer</th>
              <th style={th}>Online (h)</th>
              <th style={th}>davon Peak</th>
              <th style={th}>Annahme</th>
              <th style={th}>Fahrten gesamt</th>
              <th style={th}>Ø/Tag (Woche)</th>
              <th style={th}>Storno</th>
              <th style={th}>Bewertung</th>
              {data?.drivers?.some(d => d.status) && <th style={th}>Status</th>}
            </tr>
          </thead>
          <tbody>
            {loading && <SkeletonTableRows rows={5} cols={8} />}
            {!loading && drivers.length === 0 && (
              <tr><td style={{ ...td, textAlign: 'center', color: '#6b7280', padding: 28 }} colSpan={9}>Noch keine freigegebenen Fahrer.</td></tr>
            )}
            {!loading && drivers.map(d => {
              const p = d.performance?.schema_version===1 && d.performance?.mode==='rolling_total' ? d.performance : null;
              return (
              <tr key={d.driver_id}>
                <td style={{ ...td, fontWeight: 700 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                    <CircleDot size={12} color={d.is_online ? GREEN : '#d1d5db'} />
                    {d.name}
                  </span>
                </td>
                <td style={td}>{d.week.online_hours ?? 0}</td>
                <td style={td}>{d.week.peak_hours ?? 0}</td>
                <td style={td} title={p ? `${p.acceptance.n} / letzte ${p.acceptance.window} Angebote` : 'Aktuelle Leistung nicht verfügbar'}><Pill value={p?.acceptance.pct} suffix=" %" color={colorAccept(p?.acceptance.pct)} /></td>
                <td style={td}>{p?.rides_total ?? '–'}</td>
                <td style={td}>{d.week.rides_per_day ?? 0}</td>
                <td style={td} title={p ? `${p.cancellation.n} / letzte ${p.cancellation.window} beendete angenommene Zuweisungen` : 'Aktuelle Leistung nicht verfügbar'}><Pill value={p?.cancellation.pct} suffix=" %" color={colorCancel(p?.cancellation.pct)} /></td>
                <td style={td} title={p ? `${p.rating.rated_n} Bewertungen aus den letzten ${p.rating.rides_n} von bis zu 500 Fahrten` : 'Aktuelle Leistung nicht verfügbar'}><Pill value={p?.rating.value} suffix=" ★" color={colorRating(p?.rating.value)} /></td>
                {data?.drivers?.some(x => x.status) && (
                  <td style={td}>
                    {d.status ? (
                      <span style={{ fontSize: 12, fontWeight: 800, color: d.status.paused ? GREY : '#9c27b0' }}>
                        {d.status.tier_name}{d.status.paused ? ' (ruht)' : ''} · {d.status.points} P
                      </span>
                    ) : '–'}
                  </td>
                )}
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      <p style={{ fontSize: 12, color: '#6b7280', marginTop: 12 }}>
        Annahme zählt auch verpasste Angebote (Timeout). Storno = vom Fahrer stornierte Fahrten.
        Fenster und Beobachtungszahlen stehen an den Quoten. Bewertung aus den letzten bis zu 500 Fahrten; unbewertete Fahrten sind keine Nullsterne. „–“ = keine ausreichenden Daten.
      </p>
    </div>
  );
}
